/**
 * Freight package presets — the "kolli schablon" of P25009-450 (BR-4).
 *
 * A carrier prices a shipment on what physically travels: the package type
 * (pallet, bundle, box), its dimensions and its weight. None of that is stored
 * on a listing, but all of it is derivable from what the listing IS — a
 * complete wheel travels differently from a loose tyre — so this module maps
 * product kind + physical item count to a package preset with an assumed
 * weight. The seller confirms or corrects the weight; everything else is
 * inferred (BR-4: weight is the one field worth a manual step, because the
 * carrier re-weighs and bills the difference).
 *
 * The kilo and centimetre figures are SCHABLONS, seeded from the client's
 * shipping spec (P25009-345 §4: half pallet ~108 kg for four complete wheels,
 * ~10 kg per loose tyre, ~9 kg per rim) and the P25009-431 mockup's preset
 * cards. The spec says the final figures should come from Wheelplace's own
 * Sendify history, padded slightly upward — a low schablon is a silent margin
 * leak, because corrections are invoiced later. Adjust them HERE when that
 * history exists; nothing else hardcodes a weight.
 *
 * CommonJS with no imports, like sekFormatCore.js, because the untranspiled
 * API server requires this file directly and the browser bundles it — one
 * table, two runtimes, no drift.
 *
 * PRODUCT KIND LOOKUP: restated from util/salesUnit.js (whose server twin the
 * client cannot require and vice versa). The P25009-345 spec §7 warns this
 * naming is a trap and demands an explicit table: `businesswheel` is RIMS,
 * `business-wheel` (with the dash) is COMPLETE WHEELS. Keep the three lists
 * identical to salesUnit.js's WHEEL_TYPES/TIRE_TYPES/RIM_TYPES.
 */

const WHEEL_TYPES = ['wheel', 'business-wheel'];
const TIRE_TYPES = ['tire', 'businesstire'];
const RIM_TYPES = ['rim', 'businesswheel'];

/**
 * 'wheel' | 'tire' | 'rim' from a listing's publicData, or null when the
 * listing is not a physical wheel product (services, storage, buy requests) —
 * unlike salesUnit's getProductKind, which defaults to 'wheel', because a
 * freight preset for a non-shippable thing is a wrong answer, not a default.
 */
const resolveProductKind = (publicData = {}) => {
  const raw = publicData.adType || publicData.listingType;
  const type = raw === null || raw === undefined ? '' : String(raw).trim();
  if (WHEEL_TYPES.includes(type)) return 'wheel';
  if (TIRE_TYPES.includes(type)) return 'tire';
  if (RIM_TYPES.includes(type)) return 'rim';
  return null;
};

/* Assumed weight per physical item, in kilos. See the module comment for the
 * provenance of the figures. */
const PER_ITEM_KG = {
  wheel: 27,
  tire: 10,
  rim: 9,
};

/* The package (kolli) vocabulary. Dimensions are the OUTER measures of one
 * package of this type, in centimetres. */
const COLLI = {
  halvpall: { label: 'Halvpall', dimensionsCm: { length: 80, width: 60, height: 90 } },
  helpall: { label: 'Helpall', dimensionsCm: { length: 120, width: 80, height: 90 } },
  dackbunt: { label: 'Däckbunt', dimensionsCm: { length: 75, width: 75, height: 45 } },
  kartong: { label: 'Kartong', dimensionsCm: { length: 62, width: 62, height: 40 } },
};

/**
 * How many physical items one order line moves.
 *
 * Sharetribe's quantity counts PURCHASABLE UNITS, and for a set-priced listing
 * one unit IS four wheels — the same per-unit trap importFreight.js documents.
 * The caller resolves { salesUnit, setSize } with its runtime's salesUnit
 * module and hands the resolved pair in.
 */
const physicalItemCount = ({ quantity, salesUnit, setSize }) => {
  const units = Number(quantity);
  if (!Number.isFinite(units) || units <= 0) return 0;
  const perUnit = salesUnit === 'set' && Number.isFinite(Number(setSize)) && Number(setSize) > 0
    ? Math.round(Number(setSize))
    : 1;
  return Math.round(units) * perUnit;
};

/**
 * The package preset for one shipment: what travels, in what, and its assumed
 * weight.
 *
 * Complete wheels ride a pallet (half up to a set of four, full above);
 * loose tyres travel as bundles of two; rims as boxes of two — the parcel
 * shapes from the P25009-345 spec.
 *
 * @param {Object} params
 * @param {string} params.productKind 'wheel' | 'tire' | 'rim'
 * @param {number} params.itemCount   physical items (see physicalItemCount)
 * @returns {{productKind, colliType, colliLabel, colliCount, dimensionsCm,
 *   weightKg, perItemKg}|null} null when the kind is unknown or count is zero —
 *   the caller shows a preset-less form rather than a wrong preset.
 */
const getFreightPreset = ({ productKind, itemCount }) => {
  const perItemKg = PER_ITEM_KG[productKind];
  const count = Number(itemCount);
  if (!perItemKg || !Number.isFinite(count) || count <= 0) return null;

  let colliType;
  let colliCount;
  if (productKind === 'wheel') {
    colliType = count <= 4 ? 'halvpall' : 'helpall';
    colliCount = 1;
  } else {
    colliType = productKind === 'tire' ? 'dackbunt' : 'kartong';
    colliCount = Math.ceil(count / 2);
  }

  const colli = COLLI[colliType];
  return {
    productKind,
    colliType,
    colliLabel: colli.label,
    colliCount,
    dimensionsCm: colli.dimensionsCm,
    weightKg: Math.round(perItemKg * count),
    perItemKg,
  };
};

/** '80 × 60 × 90 cm' — the human line the preset cards and packing slip print. */
const formatDimensions = dimensionsCm => {
  if (!dimensionsCm) return '';
  const { length, width, height } = dimensionsCm;
  return `${length} × ${width} × ${height} cm`;
};

/** 'Halvpall' / '2 × Däckbunt' — one package needs no count in front of it. */
const formatColli = preset => {
  if (!preset) return '';
  return preset.colliCount > 1 ? `${preset.colliCount} × ${preset.colliLabel}` : preset.colliLabel;
};

module.exports = {
  WHEEL_TYPES,
  TIRE_TYPES,
  RIM_TYPES,
  PER_ITEM_KG,
  COLLI,
  resolveProductKind,
  physicalItemCount,
  getFreightPreset,
  formatDimensions,
  formatColli,
};
